import { useState, useEffect, useRef } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithCustomToken,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
} from 'firebase/auth';
import { auth, googleProvider, appleProvider } from '@/config/firebase';
import { clearNavigationAndFilterState } from '@/lib/authUtils';
import { Analytics } from '@/services/analytics';
import { assertAuthEmailAllowed } from '@/lib/auth-email-guard';
import { isPopupCancellation } from '@/lib/auth-popup';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  phone?: string | null;
  profileImageUrl?: string;
  role: string;
  franchiseId?: number;
  storeId?: number;
  permissions?: string[];
  isMasterDeveloper?: boolean;
  canInvite?: boolean;
  canInviteOwners?: boolean;
  isAggregateMode?: boolean;
  // True when the active franchise is a Royal Brand (see royal-brands.ts) —
  // the user only gets the restricted royalties-only app, regardless of role.
  isRoyaltyBrand?: boolean;
  // Present only during impersonation — identifies the real viewer so Sentry can
  // attribute errors to whoever is actually operating the browser, not the viewed-as account.
  _actor?: {
    id: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    profileImageUrl?: string;
  } | null;
}

// Ask the backend which OAuth provider an email is registered with. Used only
// after a failed password attempt to guide Google/Apple users to the right button.
// Returns null on any failure so the caller falls back to the generic error.
async function fetchOAuthProviderHint(email: string): Promise<'google' | 'apple' | null> {
  try {
    const res = await fetch('/api/auth/provider-hint', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) return null;
    const { provider } = await res.json();
    if (provider === 'google.com') return 'google';
    if (provider === 'apple.com') return 'apple';
    return null;
  } catch {
    return null;
  }
}

function replaceRecaptchaContainer() {
  const old = document.getElementById('recaptcha-container');
  if (old) {
    const fresh = document.createElement('div');
    fresh.id = 'recaptcha-container';
    old.replaceWith(fresh);
  }
  document.querySelector('.grecaptcha-badge')?.remove();
}

export function useFirebaseAuth() {
  const queryClient = useQueryClient();
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  // useState (not useRef) so that setting it to true immediately triggers a
  // re-render, which disables the query the instant a 401 is detected and
  // stops the stale-credential oscillation loop.
  const [hasStaleSession, setHasStaleSession] = useState(false);
  // Blocks the query while the POST to /api/auth/firebase-login is in flight.
  // Without this, onAuthStateChanged fires as soon as signInWithPopup resolves
  // (before the session is created), causing a premature 401.
  const isBackendLoginInProgress = useRef(false);
  const isSilentAuthInProgress = useRef(false);
  const mfaResolverRef = useRef<ReturnType<typeof getMultiFactorResolver> | null>(null);
  const [mfaVerificationId, setMfaVerificationId] = useState<string | null>(null);

  // Monitor Firebase auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user);
      setIsInitializing(false);
      
      if (!user) {
        queryClient.setQueryData(['/api/auth/firebase-user'], null);
        setHasStaleSession(false);
      }
    });

    return () => unsubscribe();
  }, [queryClient]);

  // Fetch user data from backend
  const { data: user, isLoading: isLoadingUser, error } = useQuery<User>({
    queryKey: ['/api/auth/firebase-user'],
    enabled: !!firebaseUser && !isInitializing && !hasStaleSession && !isBackendLoginInProgress.current && !isSilentAuthInProgress.current,
    // Retry network errors (e.g. Safari's transient "Load failed" during impersonation
    // navigation) up to 3 times before giving up. Never retry 401s — the server has
    // definitively rejected the session.
    retry: (failureCount, err: any) => {
      if (err?.message?.startsWith('401:')) return false;
      return failureCount < 3;
    },
    retryDelay: 800,
  });

  // Handle stale sessions — set circuit breaker and clean up without crashing React.
  // queryClient.clear() is intentionally avoided here: it triggers a synchronous
  // cascade of re-renders across all subscribed components which can cause
  // React's "Should have a queue" invariant violation mid-reconciliation.
  useEffect(() => {
    // 401 always means the server explicitly rejected the session.
    // "Load failed" / "Failed to fetch" are network-level errors — Safari uses "Load failed" when
    // ITP blocks a request with no valid session cookie. However, the same error fires when Safari
    // cancels in-flight requests during page navigation (e.g. after impersonation's
    // window.location.href = '/'). To avoid a false sign-out, only treat network errors as an auth
    // failure if we've never successfully fetched a user. If `user` is set, the session was recently
    // valid and this is a transient error — let the retry handle it instead of wiping the session.
    const isAuthError =
      error?.message.includes('401') ||
      (!user && (error?.message === 'Load failed' || error?.message === 'Failed to fetch'));
    if (error && firebaseUser && isAuthError) {
      setHasStaleSession(true);
      firebaseSignOut(auth);
      // Safari's ITP can prevent firebaseSignOut from completing its async
      // IndexedDB cleanup, leaving the stale credential intact across page loads.
      // Force-delete the database directly to guarantee it's gone.
      indexedDB.deleteDatabase('firebaseLocalStorageDb');
      queryClient.setQueryData(['/api/auth/firebase-user'], null);
    }
  }, [error, firebaseUser, queryClient]);

  // Sentry user context is intentionally NOT set here.
  // A single SentrySyncUser component in App.tsx owns all Sentry.setUser /
  // Sentry.setContext calls so there is only ever one instance and no race
  // between multiple mounted callers of this hook each clearing the context.

  const signInWithGoogle = async (options?: { registrationToken?: string; acceptedLegalVersion?: string }) => {
    setHasStaleSession(false);
    isBackendLoginInProgress.current = true;

    try {
      // Sign in with Google via Firebase
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();

      // Send token to backend to create the session
      const response = await fetch('/api/auth/firebase-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          idToken,
          ...(options?.registrationToken ? { registrationToken: options.registrationToken } : {}),
          ...(options?.acceptedLegalVersion ? { acceptedLegalVersion: options.acceptedLegalVersion } : {}),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.code === 'ACCOUNT_NOT_FOUND') {
          // No GoFranchise account — delete the Firebase user so it doesn't linger
          await result.user.delete().catch(() => undefined);
          await firebaseSignOut(auth);
          isBackendLoginInProgress.current = false;
          throw new Error(data.message || 'No account found.');
        } else if (data.code === 'ACCOUNT_PENDING_APPROVAL') {
          window.location.href = '/pending-approval';
          return;
        } else if (data.code === 'ACCOUNT_DISABLED') {
          window.location.href = '/account-disabled';
          return;
        }
        throw new Error(data.message || 'Login failed');
      }

      // Session is established — lift the guard and fetch user data
      isBackendLoginInProgress.current = false;
      queryClient.invalidateQueries({ queryKey: ['/api/auth/firebase-user'] });
      Analytics.login('google');

      return data.user;
    } catch (error: any) {
      isBackendLoginInProgress.current = false;

      // A closed popup is a deliberate cancellation, not a failure: no session was
      // created, so there is nothing to sign out of, and signing out here could
      // clobber a second attempt that is already succeeding.
      if (isPopupCancellation(error)) {
        throw error;
      }

      console.error('Google sign-in error:', error);

      // Sign out from Firebase if backend authentication failed
      await firebaseSignOut(auth);

      throw error;
    }
  };

  const signInWithApple = async (options?: { registrationToken?: string; acceptedLegalVersion?: string }) => {
    setHasStaleSession(false);
    isBackendLoginInProgress.current = true;

    try {
      const result = await signInWithPopup(auth, appleProvider!);
      const idToken = await result.user.getIdToken();

      const response = await fetch('/api/auth/firebase-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          idToken,
          ...(options?.registrationToken ? { registrationToken: options.registrationToken } : {}),
          ...(options?.acceptedLegalVersion ? { acceptedLegalVersion: options.acceptedLegalVersion } : {}),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.code === 'ACCOUNT_NOT_FOUND') {
          // No GoFranchise account — delete the Firebase user so it doesn't linger
          await result.user.delete().catch(() => undefined);
          await firebaseSignOut(auth);
          isBackendLoginInProgress.current = false;
          throw new Error(data.message || 'No account found.');
        } else if (data.code === 'ACCOUNT_PENDING_APPROVAL') {
          window.location.href = '/pending-approval';
          return;
        } else if (data.code === 'ACCOUNT_DISABLED') {
          window.location.href = '/account-disabled';
          return;
        } else if (data.code === 'APPLE_RELAY_VERIFICATION_REQUIRED') {
          // Apple "Hide My Email" — the Firebase account is legitimate and
          // must stay signed in (the OTP flow completes the invite using
          // this same session), so this deliberately skips the signOut
          // below. Thrown as a typed error so the caller can branch on
          // .code instead of just a message string.
          isBackendLoginInProgress.current = false;
          const relayError = new Error(data.message || 'Please verify your email.') as Error & { code: string };
          relayError.code = data.code;
          throw relayError;
        }
        throw new Error(data.message || 'Login failed');
      }

      isBackendLoginInProgress.current = false;
      queryClient.invalidateQueries({ queryKey: ['/api/auth/firebase-user'] });
      Analytics.login('apple');

      return data.user;
    } catch (error: any) {
      isBackendLoginInProgress.current = false;

      // A closed popup is a deliberate cancellation, not a failure: no session was
      // created, so there is nothing to sign out of, and signing out here could
      // clobber a second attempt that is already succeeding.
      if (isPopupCancellation(error)) {
        throw error;
      }

      // Session must survive so the OTP-verification step can complete the
      // invite using it — signing out here would strand the flow.
      if (error?.code === 'APPLE_RELAY_VERIFICATION_REQUIRED') {
        throw error;
      }

      console.error('Apple sign-in error:', error);

      await firebaseSignOut(auth);
      throw error;
    }
  };

  const signInWithEmailPassword = async (
    email: string,
    password: string,
    options?: { registrationToken?: string; acceptedLegalVersion?: string }
  ) => {
    setHasStaleSession(false);
    isBackendLoginInProgress.current = true;

    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await result.user.getIdToken();

      const response = await fetch('/api/auth/firebase-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          idToken,
          ...(options?.registrationToken ? { registrationToken: options.registrationToken } : {}),
          ...(options?.acceptedLegalVersion ? { acceptedLegalVersion: options.acceptedLegalVersion } : {}),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        isBackendLoginInProgress.current = false;
        if (data.code === 'EMAIL_NOT_VERIFIED') {
          const err = new Error('Please verify your email address before signing in.');
          (err as any).code = 'EMAIL_NOT_VERIFIED';
          throw err;
        }
        // MFA_REQUIRED uses data.error (not data.code) — see server firebaseAuth.ts
        if (data.error === 'MFA_REQUIRED') {
          // Keep Firebase signed in — auth.currentUser needed for enrollment page
          const err = new Error('Multi-factor authentication enrollment required.');
          (err as any).code = 'MFA_ENROLLMENT_REQUIRED';
          throw err;
        }
        if (data.code === 'ACCOUNT_PENDING_APPROVAL') {
          window.location.href = '/pending-approval';
          return;
        } else if (data.code === 'ACCOUNT_DISABLED') {
          window.location.href = '/account-disabled';
          return;
        }
        throw new Error(data.message || 'Login failed');
      }

      isBackendLoginInProgress.current = false;
      queryClient.invalidateQueries({ queryKey: ['/api/auth/firebase-user'] });
      Analytics.login('email');
      return data.user;
    } catch (error: any) {
      isBackendLoginInProgress.current = false;

      // MFA challenge: user has enrolled MFA; Firebase requires second factor before resolving
      if (error?.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, error);
        mfaResolverRef.current = resolver;

        if (!resolver.hints.length) {
          throw new Error('No enrolled MFA factors found. Please contact support.');
        }

        replaceRecaptchaContainer();

        const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
          size: 'invisible',
        });
        try {
          const phoneProvider = new PhoneAuthProvider(auth);
          const verificationId = await phoneProvider.verifyPhoneNumber(
            { multiFactorHint: resolver.hints[0], session: resolver.session },
            verifier
          );
          setMfaVerificationId(verificationId);
        } catch (phoneError) {
          mfaResolverRef.current = null;
          throw phoneError;
        } finally {
          verifier.clear();
          replaceRecaptchaContainer();
        }
        return; // UI checks mfaVerificationId !== null to show code input
      }

      // Propagate enrollment-required without signing out (Firebase session must survive for /enroll-mfa)
      if ((error as any).code === 'MFA_ENROLLMENT_REQUIRED') {
        throw error;
      }

      await firebaseSignOut(auth);

      const code = error?.code;
      if (code === 'auth/invalid-credential') {
        // The account may exist via Google/Apple with no password credential.
        // Ask the backend which provider it belongs to and steer the user there.
        const provider = await fetchOAuthProviderHint(email);
        if (provider) {
          const mismatch = new Error(
            provider === 'google'
              ? 'This email is registered with Google. Use the “Sign in with Google” button above.'
              : 'This email is registered with Apple. Use the “Sign in with Apple” button above.'
          );
          (mismatch as any).code = 'OAUTH_PROVIDER_MISMATCH';
          (mismatch as any).provider = provider;
          throw mismatch;
        }
        throw new Error('Invalid email or password.');
      }
      if (code === 'auth/invalid-email') throw new Error('Please enter a valid email address.');
      if (code === 'auth/too-many-requests') throw new Error('Too many attempts. Please try again later.');
      if (code === 'auth/user-disabled') {
        window.location.href = '/account-disabled';
        return;
      }

      throw error;
    }
  };

  const clearMfaChallenge = () => {
    setMfaVerificationId(null);
    mfaResolverRef.current = null;
  };

  const completeMfaSignIn = async (code: string, options?: { registrationToken?: string; acceptedLegalVersion?: string }) => {
    const resolver = mfaResolverRef.current;
    const verificationId = mfaVerificationId;
    if (!resolver || !verificationId) throw new Error('No MFA challenge in progress');

    isBackendLoginInProgress.current = true;

    try {
      const credential = PhoneAuthProvider.credential(verificationId, code);
      const assertion = PhoneMultiFactorGenerator.assertion(credential);
      const result = await resolver.resolveSignIn(assertion);
      const idToken = await result.user.getIdToken();

      const response = await fetch('/api/auth/firebase-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          idToken,
          ...(options?.registrationToken ? { registrationToken: options.registrationToken } : {}),
          ...(options?.acceptedLegalVersion ? { acceptedLegalVersion: options.acceptedLegalVersion } : {}),
        }),
      });

      // Read the body as text first so a non-JSON response (e.g. an HTML error
      // page from the edge/WAF/load balancer, or an empty body) surfaces the
      // real failure instead of a misleading "JSON.parse: unexpected character"
      // error. Only parse JSON when the body actually looks like JSON.
      const rawBody = await response.text();
      let data: any = null;
      try {
        data = rawBody ? JSON.parse(rawBody) : null;
      } catch {
        // Body was not JSON — keep data null and fall through to the error path.
      }

      if (!response.ok || data === null) {
        setMfaVerificationId(null);
        mfaResolverRef.current = null;
        if (data?.message) {
          throw new Error(data.message);
        }
        // Non-JSON / empty response: report the real HTTP status so the actual
        // cause (e.g. 403 from the WAF, 502/503 from the load balancer) is visible
        // instead of an opaque JSON.parse error.
        throw new Error(
          `Sign-in failed (server returned ${response.status} ${response.statusText || ''}`.trim() +
            `). Please try again, and contact support if this continues.`
        );
      }

      setMfaVerificationId(null);
      mfaResolverRef.current = null;
      isBackendLoginInProgress.current = false;
      queryClient.invalidateQueries({ queryKey: ['/api/auth/firebase-user'] });
      Analytics.login('email');
      return data.user;
    } catch (error: any) {
      isBackendLoginInProgress.current = false;
      const errorCode = error?.code;
      if (errorCode === 'auth/invalid-verification-code') {
        throw new Error('Incorrect code. Please try again.');
      }
      if (errorCode === 'auth/code-expired') {
        // Clear MFA state — user must restart sign-in to get a new code
        setMfaVerificationId(null);
        mfaResolverRef.current = null;
        throw new Error('Code expired. Please sign in again.');
      }
      throw error;
    }
  };

  const signUpWithEmailPassword = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    options?: { registrationToken?: string; acceptedLegalVersion?: string }
  ) => {
    setHasStaleSession(false);
    // Refuse to create a Firebase account (or send its verification email) for a
    // non-allowlisted address when the dev guard is on.
    assertAuthEmailAllowed(email);

    if (options?.registrationToken) {
      isBackendLoginInProgress.current = true;
      try {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(result.user, { displayName: `${firstName} ${lastName}`.trim() });
        const idToken = await result.user.getIdToken(true);

        const response = await fetch('/api/auth/firebase-login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            idToken,
            registrationToken: options.registrationToken,
            ...(options?.acceptedLegalVersion ? { acceptedLegalVersion: options.acceptedLegalVersion } : {}),
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          if (data.code === 'ACCOUNT_PENDING_APPROVAL') {
            window.location.href = '/pending-approval';
            return;
          } else if (data.code === 'ACCOUNT_DISABLED') {
            window.location.href = '/account-disabled';
            return;
          }
          throw new Error(data.message || 'Sign up failed');
        }

        isBackendLoginInProgress.current = false;
        queryClient.invalidateQueries({ queryKey: ['/api/auth/firebase-user'] });
        return data.user;
      } catch (error: any) {
        isBackendLoginInProgress.current = false;
        await firebaseSignOut(auth);

        const code = error?.code;
        if (code === 'auth/invalid-email') throw new Error('Please enter a valid email address.');
        if (code === 'auth/email-already-in-use') throw new Error('An account with this email already exists. Try signing in instead.');
        if (code === 'auth/weak-password') throw new Error('Password must be at least 6 characters.');
        if (code === 'auth/too-many-requests') throw new Error('Too many attempts. Please try again later.');
        throw error;
      }
    } else {
      isSilentAuthInProgress.current = true;
      try {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(result.user, { displayName: `${firstName} ${lastName}`.trim() });
        await sendEmailVerification(result.user).catch(() => undefined);
        await firebaseSignOut(auth);
        isSilentAuthInProgress.current = false;
      } catch (error: any) {
        await firebaseSignOut(auth).catch(() => undefined);
        isSilentAuthInProgress.current = false;

        const code = error?.code;
        if (code === 'auth/invalid-email') throw new Error('Please enter a valid email address.');
        if (code === 'auth/email-already-in-use') throw new Error('An account with this email already exists. Try signing in instead.');
        if (code === 'auth/weak-password') throw new Error('Password must be at least 6 characters.');
        if (code === 'auth/too-many-requests') throw new Error('Too many attempts. Please try again later.');
        throw error;
      }
    }
  };

  // Signs in with a Firebase custom token (minted server-side after OTP
  // verification for the invite-acceptance flow). The invite itself is
  // already completed server-side by the time this runs (verify-otp calls
  // completeFranchiseeRegistration before returning the custom token), so
  // this is a normal sign-in — passing registrationToken here would try to
  // complete the same invite a second time and fail with LINK_EXPIRED since
  // its status is no longer 'link_created'.
  const completeInviteWithCustomToken = async (customToken: string) => {
    isBackendLoginInProgress.current = true;
    try {
      const result = await signInWithCustomToken(auth, customToken);
      const idToken = await result.user.getIdToken(true);

      const response = await fetch('/api/auth/firebase-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ idToken }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Sign in failed');
      }

      isBackendLoginInProgress.current = false;
      queryClient.invalidateQueries({ queryKey: ['/api/auth/firebase-user'] });
      return data.user;
    } catch (error) {
      isBackendLoginInProgress.current = false;
      await firebaseSignOut(auth).catch(() => undefined);
      throw error;
    }
  };

  // Finishes the Apple "Hide My Email" invite flow. The Firebase popup
  // already signed the user in (auth.currentUser is live) — the first
  // /api/auth/firebase-login attempt just failed with
  // APPLE_RELAY_VERIFICATION_REQUIRED because the relay email didn't match
  // the invite, so no server session exists yet. The invite is now already
  // completed server-side by verify-otp (no registrationToken here, same
  // reasoning as completeInviteWithCustomToken above), so this just retries
  // the login with a fresh ID token from the still-live session.
  const completeAppleRelayInvite = async () => {
    isBackendLoginInProgress.current = true;
    try {
      if (!auth?.currentUser) {
        throw new Error('Your session has expired. Please sign in again.');
      }
      const idToken = await auth.currentUser.getIdToken(true);

      const response = await fetch('/api/auth/firebase-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ idToken }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Sign in failed');
      }

      isBackendLoginInProgress.current = false;
      queryClient.invalidateQueries({ queryKey: ['/api/auth/firebase-user'] });
      return data.user;
    } catch (error) {
      isBackendLoginInProgress.current = false;
      await firebaseSignOut(auth).catch(() => undefined);
      throw error;
    }
  };

  const sendPasswordReset = async (email: string): Promise<void> => {
    assertAuthEmailAllowed(email);
    await sendPasswordResetEmail(auth, email);
  };

  const resendVerificationEmail = async (email: string, password: string): Promise<void> => {
    assertAuthEmailAllowed(email);
    isSilentAuthInProgress.current = true;
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(result.user);
      await firebaseSignOut(auth);
    } catch (error: any) {
      await firebaseSignOut(auth).catch(() => undefined);
      // MFA-enrolled users cannot use this flow — their email is already verified
      if (error?.code === 'auth/multi-factor-auth-required') {
        throw new Error('Your email is already verified. Please sign in normally.');
      }
      if (error?.code === 'auth/too-many-requests') {
        throw new Error('Too many requests. Please wait a few minutes before trying again.');
      }
      throw error;
    } finally {
      isSilentAuthInProgress.current = false;
    }
  };

  const signOut = async () => {
    Analytics.logout();
    try {
      // Sign out from backend
      await fetch('/api/auth/firebase-logout', {
        method: 'POST',
        credentials: 'include',
      });

      // Sign out from Firebase
      await firebaseSignOut(auth);

      // Clear all cached data
      queryClient.clear();
      clearNavigationAndFilterState();

      // Redirect to home
      window.location.href = '/';
    } catch (error) {
      console.error('Sign out error:', error);
      // Force sign out even if backend call fails
      await firebaseSignOut(auth);
      queryClient.clear();
      clearNavigationAndFilterState();
      window.location.href = '/';
    }
  };

  return {
    user,
    firebaseUser,
    isLoading: isInitializing || isLoadingUser,
    isAuthenticated: !!user && !!firebaseUser,
    signInWithGoogle,
    signInWithApple,
    signInWithEmailPassword,
    completeMfaSignIn,
    clearMfaChallenge,
    mfaVerificationId,
    signUpWithEmailPassword,
    completeInviteWithCustomToken,
    completeAppleRelayInvite,
    sendPasswordReset,
    resendVerificationEmail,
    signOut,
  };
}
